import { Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthProvider'
import Layout from './components/Layout'
import AuthGate from './components/AuthGate'
import ErrorBoundary from './components/ErrorBoundary'
import Home from './pages/Home'
import About from './pages/About'
import Mission from './pages/Mission'
import Donate from './pages/Donate'
import Docs from './pages/Docs'
import FoundersWall from './pages/FoundersWall'
import BrowseRepositories from './pages/BrowseRepositories'
import VersionView from './pages/VersionView'
import ContributorDashboard from './pages/ContributorDashboard'
import DashboardSubmissions from './pages/DashboardSubmissions'
import RepositoryCommits from './pages/RepositoryCommits'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword from './pages/ResetPassword'
import VerifyEmail from './pages/VerifyEmail'
import ConfirmEmailChange from './pages/ConfirmEmailChange'
import Welcome from './pages/Welcome'
import ReferralRedirect from './pages/ReferralRedirect'

const RepositoryDetail = lazy(() => import('./pages/RepositoryDetail'))
const UserProfile = lazy(() => import('./pages/UserProfile'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))

function LazyRouteFallback() {
  return (
    <div className="page-container" style={{ padding: '2.5rem 1rem', textAlign: 'center' }}>
      <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono, monospace)', fontSize: '0.9rem' }}>
        Loading…
      </p>
    </div>
  )
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <Layout>
          <Suspense fallback={<LazyRouteFallback />}>
          <Routes>
            {/* Public Landing Page - redirects to dashboard if logged in */}
            <Route path="/" element={
              <AuthGate mode="guestOnly">
                <Home />
              </AuthGate>
            } />
            
            {/* Public Static Pages */}
            <Route path="/about" element={<About />} />
            <Route path="/mission" element={<Mission />} />
            <Route path="/donate" element={<Donate />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/founders" element={<FoundersWall />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
            {/* Direct-link landing (not linked from main nav) */}
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/r/:code" element={<ReferralRedirect />} />
            
            {/* Public Application Routes */}
            <Route path="/repositories" element={<BrowseRepositories />} />
            <Route path="/repositories/:id/commits" element={<RepositoryCommits />} />
            <Route path="/repositories/:id" element={<RepositoryDetail />} />
            <Route path="/versions/:id" element={<VersionView />} />
            
            <Route path="/users/:id" element={
              <AuthGate mode="requireAuth">
                <UserProfile />
              </AuthGate>
            } />
            
            {/* Protected Routes - require authentication */}
            <Route path="/dashboard/submissions" element={
              <AuthGate mode="requireAuth">
                <DashboardSubmissions />
              </AuthGate>
            } />
            <Route path="/dashboard" element={
              <AuthGate mode="requireAuth">
                <ContributorDashboard />
              </AuthGate>
            } />
            <Route path="/admin" element={
              <AuthGate mode="admin">
                <AdminDashboard />
              </AuthGate>
            } />
          </Routes>
          </Suspense>
        </Layout>
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  )
}

export default App

